import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import toast from 'react-hot-toast';
import { teacherModuleAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  LeaveRequestDetailsDialog,
  LeaveRequestListCard,
} from '../../components/Teacher/LeaveRequestDetails';

const TeacherLeave = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [detailRequest, setDetailRequest] = useState(null);
  const [form, setForm] = useState({
    leaveDate: new Date().toISOString().slice(0, 10),
    scope: 'full_day',
    reason: '',
    batchIds: [],
  });

  const myLeaveQuery = useQuery(['teacherMyLeaveRequests'], async () => {
    const res = await teacherModuleAPI.getMyLeaveRequests();
    return res.data?.data || [];
  });

  const batchesQuery = useQuery(
    ['teacherBatchesForLeave', form.leaveDate],
    async () => {
      const res = await teacherModuleAPI.getLeaveBatchesForDate({ date: form.leaveDate });
      return res.data?.data || [];
    },
    { enabled: form.scope === 'specific_batches' }
  );

  const submitMutation = useMutation((payload) => teacherModuleAPI.createLeaveRequest(payload), {
    onSuccess: () => {
      toast.success('Leave request submitted');
      queryClient.invalidateQueries(['teacherMyLeaveRequests']);
      setForm((prev) => ({ ...prev, reason: '', batchIds: [] }));
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to submit leave'),
  });

  const handleSubmit = () => {
    if (!form.reason.trim()) {
      toast.error('Please add a reason');
      return;
    }
    if (form.scope === 'specific_batches' && form.batchIds.length === 0) {
      toast.error('Please select at least one batch');
      return;
    }
    submitMutation.mutate(form);
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Leave Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Submit leave requests for supervisor approval. Track status and decision details below.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <TextField
            label="Leave Date"
            type="date"
            value={form.leaveDate}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setForm((prev) => ({ ...prev, leaveDate: e.target.value }))}
            sx={{ maxWidth: 260 }}
          />
          <TextField
            select
            label="Scope"
            value={form.scope}
            onChange={(e) => setForm((prev) => ({ ...prev, scope: e.target.value, batchIds: [] }))}
            sx={{ maxWidth: 260 }}
          >
            <MenuItem value="full_day">Full Day</MenuItem>
            <MenuItem value="specific_batches">Specific Batches</MenuItem>
          </TextField>
          {form.scope === 'specific_batches' ? (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Batches for this leave day
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Select one or more batches where you are primary or shadow teacher.
              </Typography>
              {batchesQuery.isLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                  <CircularProgress size={22} />
                  <Typography variant="body2" color="text.secondary">
                    Loading your batches…
                  </Typography>
                </Box>
              ) : null}
              {batchesQuery.isError ? (
                <Typography color="error" variant="body2">
                  Could not load batches. Try again or contact support.
                </Typography>
              ) : null}
              {!batchesQuery.isLoading && !batchesQuery.isError ? (
                <Paper variant="outlined" sx={{ maxHeight: 280, overflow: 'auto', p: 1 }}>
                  {(batchesQuery.data || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No primary or shadow batches are scheduled for this date.
                    </Typography>
                  ) : (
                    <FormGroup>
                      {(batchesQuery.data || []).map((b) => {
                        const id = String(b.id);
                        const roleLabel = b.role === 'shadow' ? 'Shadow teacher' : 'Primary';
                        const startBit = b.start_date ? ` · starts ${b.start_date}` : '';
                        return (
                          <FormControlLabel
                            key={id}
                            control={
                              <Checkbox
                                checked={form.batchIds.some((x) => String(x) === id)}
                                onChange={(_, checked) => {
                                  setForm((prev) => ({
                                    ...prev,
                                    batchIds: checked
                                      ? [...prev.batchIds.filter((x) => String(x) !== id), id]
                                      : prev.batchIds.filter((x) => String(x) !== id),
                                  }));
                                }}
                              />
                            }
                            label={`${b.name}${startBit} · ${roleLabel}`}
                          />
                        );
                      })}
                    </FormGroup>
                  )}
                </Paper>
              ) : null}
            </Box>
          ) : null}
          <TextField
            label="Reason"
            multiline
            minRows={3}
            value={form.reason}
            onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))}
          />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={handleSubmit} disabled={submitMutation.isLoading}>
              Submit Request
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          My Leave Requests
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your submitted leave with status and supervisor decisions.
        </Typography>
        {(myLeaveQuery.data || []).map((r) => (
          <LeaveRequestListCard
            key={r.id}
            request={r}
            authUser={user}
            onViewDetails={setDetailRequest}
          />
        ))}
        {(myLeaveQuery.data || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No leave requests yet.
          </Typography>
        ) : null}
      </Paper>

      <LeaveRequestDetailsDialog
        open={Boolean(detailRequest)}
        onClose={() => setDetailRequest(null)}
        request={detailRequest}
        authUser={user}
      />
    </Box>
  );
};

export default TeacherLeave;
